import { useAuthStore } from '../stores/auth';

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:3000';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: ApiOptions = {}, retry = true): Promise<T> {
    const { accessToken } = useAuthStore.getState();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const url = `${this.baseUrl.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    // Handle 401 Unauthorized - token expired
    if (response.status === 401 && retry) {
      console.log('Token expired, attempting to refresh...');

      try {
        // Try to refresh the token
        await this.refreshToken();

        // Retry the request with the new token (only once)
        return this.request<T>(endpoint, options, false);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);

        // Redirect to login page
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          // Clear auth state
          useAuthStore.getState().logout();
          // Redirect to login with return url
          window.location.href = `/login?returnUrl=${encodeURIComponent(currentPath)}`;
        }

        throw new Error('Session expired. Please login again.');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Refresh the authentication token
   */
  private async refreshToken(): Promise<void> {
    const { fetchAuthSession } = await import('aws-amplify/auth');

    // Force refresh the session
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('Failed to refresh token');
    }

    // Update the token in the auth store
    useAuthStore.setState({ accessToken: idToken });

    console.log('Token refreshed successfully');
  }

  // Chat endpoints
  sendMessage = async (
    message: string,
    conversationId?: string,
    history?: Array<{ role: 'user' | 'assistant'; content: string }>
  ) => {
    // Add datetime context to help agent understand relative dates
    const now = new Date();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateContext = {
      currentDateTime: now.toISOString(),
      localDate: now.toLocaleDateString('en-CA'), // YYYY-MM-DD format
      localTime: now.toLocaleTimeString('en-US', { hour12: false }),
      timezone,
      dayOfWeek: now.toLocaleDateString('en-US', { weekday: 'long' }),
    };

    return this.request<{
      message: string;
      agentUsed: string;
      actions?: Array<{ type: string; data: unknown }>;
      conversationId: string;
    }>('/chat', {
      method: 'POST',
      body: { message, conversationId, history, dateContext },
    });
  };

  // Task endpoints
  getTasks = async (filters?: { status?: string; priority?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.priority) params.set('priority', filters.priority);

    const query = params.toString();
    return this.request<{ success: boolean; data: Task[] }>(
      `/tasks${query ? `?${query}` : ''}`
    );
  };

  createTask = async (task: CreateTaskInput) => {
    return this.request<{ success: boolean; data: Task }>('/tasks', {
      method: 'POST',
      body: task,
    });
  };

  updateTask = async (taskId: string, updates: Partial<Task>) => {
    return this.request<{ success: boolean; data: Task }>(`/tasks/${taskId}`, {
      method: 'PUT',
      body: updates,
    });
  };

  deleteTask = async (taskId: string) => {
    return this.request<{ success: boolean }>(`/tasks/${taskId}`, {
      method: 'DELETE',
    });
  };

  // Calendar endpoints
  getEvents = async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);

    const query = params.toString();
    return this.request<{ success: boolean; data: CalendarEventsResponse }>(
      `/calendar/events${query ? `?${query}` : ''}`
    );
  };

  createEvent = async (event: CreateEventInput) => {
    return this.request<{ success: boolean; data: CalendarEvent }>('/calendar/events', {
      method: 'POST',
      body: event,
    });
  };

  // Planner endpoints
  getDailyPlan = async (date: string) => {
    return this.request<{ success: boolean; data: DailyPlan }>(`/planner?date=${date}`);
  };

  createPlan = async (options: { action: string; date?: string; startDate?: string; goals?: string[] }) => {
    return this.request<{ success: boolean; data: unknown }>('/planner', {
      method: 'POST',
      body: options,
    });
  };

  // Personal data endpoints
  getPersonalData = async (category?: string) => {
    const query = category ? `?category=${category}` : '';
    return this.request<{ success: boolean; data: { items: PersonalData[] } }>(`/data${query}`);
  };

  savePersonalData = async (data: { key: string; value: unknown; category: string }) => {
    return this.request<{ success: boolean; data: PersonalData }>('/data', {
      method: 'POST',
      body: data,
    });
  };

  // Google OAuth endpoints
  initiateGoogleAuth = async () => {
    return this.request<{ authUrl: string }>('/auth/google');
  };

  completeGoogleAuth = async (code: string) => {
    return this.request<{ success: boolean; message: string }>('/auth/google/callback', {
      method: 'POST',
      body: { code },
    });
  };

  getGoogleConnectionStatus = async () => {
    return this.request<{ connected: boolean; expiresAt?: number }>('/auth/google/status');
  };

  disconnectGoogle = async () => {
    return this.request<{ success: boolean; message: string }>('/auth/google/disconnect', {
      method: 'POST',
    });
  };

  // Profile endpoints
  getProfile = async () => {
    return this.request<{ success: boolean; data: User }>('/auth/profile');
  };

  updateProfile = async (name: string) => {
    return this.request<{ success: boolean; data: User }>('/auth/profile', {
      method: 'POST',
      body: { name },
    });
  };

  // Sync endpoints - for syncing with Google Calendar/Tasks

  /**
   * Trigger a sync operation (fire-and-forget style)
   * This syncs calendar events and tasks from connected providers
   */
  sync = async (options?: SyncOptions) => {
    return this.request<SyncResponse>('/sync', {
      method: 'POST',
      body: options || {},
    });
  };

  /**
   * Get sync status and connected providers
   */
  getSyncStatus = async () => {
    return this.request<SyncStatusResponse>('/sync/status');
  };
}

// Types

// Source tracking for multi-provider support
export type TaskSource = 'local' | 'google' | 'microsoft' | 'todoist';
export type EventSource = 'local' | 'google' | 'microsoft' | 'zoom';

export interface CalendarEventsResponse {
  events: CalendarEvent[];
  syncError?: string;
  isStale?: boolean;
}

export interface User {
  userId: string;
  email: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Task {
  userId: string;
  taskId: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;

  // Provider tracking
  source?: TaskSource;
  providerId?: string;
  providerListId?: string;

  // Rich metadata
  notes?: string;
  links?: Array<{ url: string; title?: string }>;

  // Sync metadata
  lastSyncedAt?: string;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  priority?: Task['priority'];
  status?: Task['status'];
  dueDate?: string;
  tags?: string[];
}

// Attendee with rich metadata
export interface Attendee {
  email: string;
  name?: string;
  status?: 'accepted' | 'declined' | 'tentative' | 'pending' | 'needsAction';
  self?: boolean;
  organizer?: boolean;
}

// Conference/Meeting data
export interface ConferenceData {
  type: 'meet' | 'teams' | 'zoom' | 'webex' | 'other';
  joinUrl?: string;
  dialIn?: string;
  solutionName?: string;
}

export interface CalendarEvent {
  userId: string;
  eventId: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  attendees?: string[] | Attendee[];
  isAllDay?: boolean;

  // Provider tracking
  source?: EventSource;
  providerId?: string;
  googleEventId?: string;

  // Rich metadata
  organizer?: { email: string; displayName?: string; self?: boolean };
  conferenceData?: ConferenceData;
  meetingLink?: string;
  htmlLink?: string;
  eventStatus?: 'confirmed' | 'tentative' | 'cancelled';

  // Sync metadata
  lastSyncedAt?: string;
}

export interface CreateEventInput {
  title: string;
  startTime: string;
  endTime: string;
  description?: string;
  location?: string;
  attendees?: string[];
}

export interface DailyPlan {
  date: string;
  timeBlocks: Array<{
    start: string;
    end: string;
    activity: string;
    type: 'task' | 'event' | 'break' | 'buffer';
  }>;
  summary: string;
  tips: string[];
}

export interface PersonalData {
  userId: string;
  dataKey: string;
  category: string;
  value: unknown;
  createdAt: string;
  updatedAt: string;
}

// Sync types
export interface SyncOptions {
  resources?: Array<'calendar' | 'tasks'>;
  fullSync?: boolean;
  mode?: 'blocking' | 'async';
}

export interface SyncResult {
  provider: string;
  resource: 'calendar' | 'tasks';
  success: boolean;
  itemsCreated?: number;
  itemsUpdated?: number;
  itemsDeleted?: number;
  error?: string;
  syncedAt: string;
}

export interface SyncResponse {
  success: boolean;
  data: {
    success: boolean;
    calendar: SyncResult[];
    tasks: SyncResult[];
    duration: number;
    syncedAt: string;
  };
  message?: string;
}

export interface ProviderStatus {
  name: string;
  type: 'calendar' | 'tasks' | 'both';
  connected: boolean;
}

export interface SyncStatusResponse {
  success: boolean;
  data: {
    providers: ProviderStatus[];
  };
}

export const api = new ApiClient(API_ENDPOINT);

